import { useEffect, useRef, useCallback } from 'react'
import { useElementWidth } from '../../hooks/useElementWidth'
import { Loader2 } from 'lucide-react'
import MessageRow from './MessageRow'

const dayKey = (iso) => String(iso || '').slice(0, 10)
const dayLabel = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const today = new Date()
  const yest = new Date(Date.now() - 86400000)
  const same = (a, b) => a.toDateString() === b.toDateString()
  if (same(d, today)) return 'Bugün'
  if (same(d, yest)) return 'Dün'
  return d.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })
}

// Mesaj akışı — tarih ayırıcı, yukarı kaydırınca eski sayfa, prepend'de scroll korunur
export default function MessageList({
  messages = [], taskRefs = {}, mentionRefs = {}, hasMore, isLoading, isFetchingOlder, onLoadOlder,
  onReact, onReply, onEdit, onDelete, onOpenThread, onOpenProfile, onQuoteJump, onAtBottom, highlightId, myUserId, company, channelKind,
  hideActions, channelId,
}) {
  const ref = useRef(null)
  const listWidth = useElementWidth(ref)   // çubuk düğme sayısı buna göre
  const anchorRef = useRef(null)     // prepend öncesi scrollHeight
  const stickBottom = useRef(true)
  const lastCount = useRef(0)

  // KANAL DEĞİŞİNCE en alta dön. 'stickBottom' bir ref ve bileşen kanal değişiminde
  // yeniden kurulmuyor: önceki sohbette yukarı kaydırıp eski mesaj okuyan kullanıcıda
  // bayrak false kalıyordu ve yeni açılan sohbet EN ÜSTTEN (en eski mesajdan)
  // açılıyordu (2026-08-27 DM bildirimi). Sayaçlar da sıfırlanmalı, yoksa yeni
  // kanalın mesajları "yeni gelmiş" sayılıp okundu işareti tetikleniyor.
  useEffect(() => {
    stickBottom.current = true
    anchorRef.current = null
    lastCount.current = 0
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [channelId])

  // Yeni mesaj geldiğinde: en alttaysak alta yapış; eski sayfa eklendiyse konumu koru
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (anchorRef.current != null) {
      el.scrollTop = el.scrollHeight - anchorRef.current
      anchorRef.current = null
    } else if (stickBottom.current) {
      el.scrollTop = el.scrollHeight
      if (messages.length > lastCount.current) onAtBottom?.()
    }
    lastCount.current = messages.length
  }, [messages, onAtBottom])

  // Vurgulanan mesaja kaydır (arama zıplaması)
  useEffect(() => {
    if (!highlightId) return
    const node = ref.current?.querySelector(`[data-mid="${highlightId}"]`)
    if (node) node.scrollIntoView({ block: 'center' })
  }, [highlightId, messages])

  const onScroll = useCallback((e) => {
    const el = e.currentTarget
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60
    if (stickBottom.current) onAtBottom?.()
    if (el.scrollTop < 80 && hasMore && !isFetchingOlder) {
      anchorRef.current = el.scrollHeight
      onLoadOlder?.()
    }
  }, [hasMore, isFetchingOlder, onLoadOlder, onAtBottom])

  if (isLoading) {
    return <div className="flex-1 flex items-center justify-center text-gray-400"><Loader2 size={18} className="animate-spin" /></div>
  }

  let prevDay = null
  return (
    <div ref={ref} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-3">
      {isFetchingOlder && <div className="py-2 text-center text-gray-400"><Loader2 size={14} className="animate-spin mx-auto" /></div>}
      {messages.length === 0 && (
        <div className="py-16 text-center text-sm text-gray-400">Henüz mesaj yok. İlk mesajı sen yaz.</div>
      )}
      {messages.map((m) => {
        const dk = dayKey(m.created_at)
        const showDay = dk !== prevDay
        prevDay = dk
        return (
          <div key={m.id}>
            {showDay && (
              <div className="sticky top-1 z-[1] flex items-center justify-center my-3 pointer-events-none">
                <span className="px-3 py-1 rounded-full border border-gray-200 bg-white/95 text-[11px] text-gray-500 shadow-sm pointer-events-auto">
                  {dayLabel(m.created_at)}
                </span>
              </div>
            )}
            <MessageRow message={m} taskRefs={taskRefs} mentionRefs={mentionRefs} myUserId={myUserId} company={company} channelKind={channelKind}
              highlight={String(highlightId) === String(m.id)}
              onReact={onReact} onReply={onReply} onEdit={onEdit} onDelete={onDelete} onOpenThread={onOpenThread} onOpenProfile={onOpenProfile} onQuoteJump={onQuoteJump}
              hideActions={hideActions} rowWidth={listWidth} />
          </div>
        )
      })}
    </div>
  )
}
